/**
 * WHO OWNS A RUN'S NOTES WHILE IT IS STILL GOING.
 *
 * An exposure run is recorded as it happens: `ExpositionStarted`, then one `ExpositionReactionRecorded`
 * per reading, then `ExpositionFinished`. The NOTES are carried by that last fact alone, so for the
 * whole live part of a run the server has nothing to say about them — the field belongs to the person
 * typing in it.
 *
 * A surface that re-reads the run after each reading (which it must, to show the reading the server
 * actually recorded) therefore has to keep its own notes rather than take the server's empty ones.
 * Mobile did not, and the founder found it on 2026-09-08: *"the notes area is reset every time I click
 * add reaction … the card for the session I created from mobile has empty notes, though I have been
 * adding them every time."* Finish then sent the field it had just cleared, so the notes were not
 * merely hidden — they were never recorded.
 *
 * The rule is deliberately one-directional: a server value wins whenever it holds anything, so a
 * finished run, a correction, or a run edited on the other surface still replaces what is on screen.
 */

type RunWithNotes = {
  expositionId?: string | null;
  reactionNotes?: string | null;
};

const hasText = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Merge the server's runs with the notes the person has typed but not yet finished.
 *
 * Runs are matched by `expositionId`; a run without one (never started) is left exactly as the
 * server sent it, and so is a run whose server notes are non-empty.
 */
export function preserveUnsavedRunNotes<T extends RunWithNotes>(
  serverRuns: readonly T[],
  localRuns: readonly RunWithNotes[],
): T[] {
  return serverRuns.map((run) => {
    if (!run.expositionId || hasText(run.reactionNotes)) {
      return run;
    }

    const local = localRuns.find(
      (candidate) =>
        Boolean(candidate.expositionId) &&
        candidate.expositionId === run.expositionId,
    );

    return local && hasText(local.reactionNotes)
      ? { ...run, reactionNotes: local.reactionNotes as string }
      : run;
  });
}
